import { useMemo } from 'react'

interface YTDPerformanceInput {
	ytdPriceOpen: string | number
	ytdPrice: string | number
}

/**
 * Converts a string or number to a number, handling edge cases
 * @param value - The value to convert
 * @returns A number or 0 if conversion fails
 */
const toNumber = (value: string | number): number => {
	if (typeof value === 'number') return value
	const parsed = parseFloat(value)
	return isNaN(parsed) ? 0 : parsed
}

/**
 * Calculates YTD performance percentage from open and current prices
 * @param ytdPriceOpen - The price at the start of the year (string or number)
 * @param ytdPrice - The current price (string or number)
 * @returns The YTD performance as a decimal (e.g., 0.15 for 15% gain)
 */
export const calculateYTDPerformance = ({ ytdPriceOpen, ytdPrice }: YTDPerformanceInput): number => {
	const open = toNumber(ytdPriceOpen)
	const current = toNumber(ytdPrice)

	if (!open || open === 0) return 0
	return (current - open) / open
}

/**
 * Hook to calculate YTD performance with memoization
 * @param ytdPriceOpen - The price at the start of the year (string or number)
 * @param ytdPrice - The current price (string or number)
 * @returns The YTD performance as a decimal
 */
export const useYTDPerformance = (ytdPriceOpen: string | number, ytdPrice: string | number): number => {
	return useMemo(() => calculateYTDPerformance({ ytdPriceOpen, ytdPrice }), [ytdPriceOpen, ytdPrice])
}
